import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { STATUS } from 'constants/miscConstants';
import AdminService from 'services/AdminService';
import { t } from 'i18next';

const { SUCCESS, ERROR } = STATUS;

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  status: SUCCESS,
  data: { paymentPlans: [], totalPages: 0, totalCount: 0 },
  filters: {
    pageNumber: 1,
    pageSize: 10,
    search: '',
    purchasable: null,
    billingCycle: null,
  },
  submitting: false,
  actionSuccess: false,
  // Form metadata (categories, billing cycles, reusable sub-features, route catalogue). Fetched once
  // when the screen mounts — it is static for the life of the session.
  catalog: null,
  catalogLoading: false,
};

export const getPaymentPackages = createAsyncThunk('paymentPackages/getPaymentPackages', async (data, { rejectWithValue }) => {
  const { queryParams, pageNumber, pageSize, search, purchasable, billingCycle } = data;
  try {
    const response = await AdminService.getPaymentPlans(queryParams);
    return { response, pageNumber, pageSize, search, purchasable, billingCycle };
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const getPaymentPackageCatalog = createAsyncThunk('paymentPackages/getPaymentPackageCatalog', async (_, { rejectWithValue }) => {
  try {
    return await AdminService.getPaymentPlanCatalog();
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to load package catalog');
  }
});

export const createPaymentPackage = createAsyncThunk('paymentPackages/createPaymentPackage', async (data, { rejectWithValue }) => {
  try {
    return await AdminService.createPaymentPlan(data);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to create package');
  }
});

export const updatePaymentPackage = createAsyncThunk('paymentPackages/updatePaymentPackage', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await AdminService.updatePaymentPlan(id, data);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to update package');
  }
});

export const updatePaymentPackagePurchasable = createAsyncThunk('paymentPackages/updatePaymentPackagePurchasable', async ({ id, purchasable }, { rejectWithValue }) => {
  try {
    const response = await AdminService.updatePaymentPlanPurchasable(id, purchasable);
    return { response, purchasable };
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to update package status');
  }
});

const setSuccess = (state, message) => {
  state.submitting = false;
  state.status = SUCCESS;
  state.message = message;
  state.showMessage = true;
  state.actionSuccess = true;
};

const setFailure = (state, action) => {
  state.submitting = false;
  state.status = ERROR;
  state.message = action.payload;
  state.showMessage = true;
};

export const paymentPackagesSlice = createSlice({
  name: 'paymentPackages',
  initialState,
  reducers: {
    hideMessage: (state) => {
      state.message = '';
      state.status = SUCCESS;
      state.showMessage = false;
      state.actionSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentPackages.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPaymentPackages.fulfilled, (state, action) => {
        const { response, pageNumber, pageSize, search, purchasable, billingCycle } = action.payload;
        state.data = response || { paymentPlans: [], totalPages: 0, totalCount: 0 };
        state.filters.pageNumber = pageNumber;
        state.filters.pageSize = pageSize;
        state.filters.search = search;
        state.filters.purchasable = purchasable;
        state.filters.billingCycle = billingCycle;
        state.loading = false;
      })
      .addCase(getPaymentPackages.rejected, (state, action) => {
        state.loading = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(getPaymentPackageCatalog.pending, (state) => {
        state.catalogLoading = true;
      })
      .addCase(getPaymentPackageCatalog.fulfilled, (state, action) => {
        state.catalog = action.payload || null;
        state.catalogLoading = false;
      })
      .addCase(getPaymentPackageCatalog.rejected, (state, action) => {
        state.catalogLoading = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(createPaymentPackage.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createPaymentPackage.fulfilled, (state) => {
        setSuccess(state, t('component.paymentPackages.message.created'));
      })
      .addCase(createPaymentPackage.rejected, (state, action) => {
        setFailure(state, action);
      })

      .addCase(updatePaymentPackage.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updatePaymentPackage.fulfilled, (state) => {
        setSuccess(state, t('component.paymentPackages.message.updated'));
      })
      .addCase(updatePaymentPackage.rejected, (state, action) => {
        setFailure(state, action);
      })

      .addCase(updatePaymentPackagePurchasable.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updatePaymentPackagePurchasable.fulfilled, (state, action) => {
        const { purchasable } = action.payload;
        setSuccess(state, purchasable ? t('component.paymentPackages.message.restored') : t('component.paymentPackages.message.withdrawn'));
      })
      .addCase(updatePaymentPackagePurchasable.rejected, (state, action) => {
        setFailure(state, action);
      });
  },
});

export const { hideMessage } = paymentPackagesSlice.actions;

export default paymentPackagesSlice.reducer;
